class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.endTime = new Date(this.getAttribute("end-time"));
    this.dayElement = this.querySelector('[data-target="day"]');
    this.hourElement = this.querySelector('[data-target="hour"]');
    this.minuteElement = this.querySelector('[data-target="minute"]');
    this.secondElement = this.querySelector('[data-target="second"]');
    this.updateTimer();
    this.interval = setInterval(() => this.updateTimer(), 1000);
  }

  updateTimer() {
    const milisecDiff = this.endTime - new Date();
    if (milisecDiff <= 0) {
      this.dayElement.textContent = "00";
      this.hourElement.textContent = "00";
      this.minuteElement.textContent = "00";
      this.secondElement.textContent = "00";
      clearInterval(this.interval);
      this.style.display = "none";
      return;
    }

    const dateDiff = new Date(milisecDiff);
    const days = Math.floor(milisecDiff / (1000 * 60 * 60 * 24));
    const hours = dateDiff.getUTCHours();
    const minutes = dateDiff.getUTCMinutes();
    const seconds = dateDiff.getUTCSeconds();

    this.dayElement.textContent = days.toString().padStart(2, "0");
    this.hourElement.textContent = hours.toString().padStart(2, "0");
    this.minuteElement.textContent = minutes.toString().padStart(2, "0");
    this.secondElement.textContent = seconds.toString().padStart(2, "0");
  }
}

customElements.define("countdown-timer", CountdownTimer);

/**
 * Developer note: QuantityButton custom element for cart line item quantity controls.
 * This enables the -/+ buttons to increment/decrement the quantity input.
 * The decrement button at minimum quantity is rendered as an <a> tag that removes the item,
 * so this element only handles the button-based increment/decrement.
 */
class QuantityButton extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener("click", this._onClick.bind(this));
  }

  _onClick(event) {
    event.preventDefault();
    
    // Find the associated quantity input within the same line-item-quantity container
    const container = this.closest("line-item-quantity");
    if (!container) return;
    
    const action = this.dataset.action;
    
    // Handle remove action - removes the item from cart via AJAX
    if (action === "remove") {
      const lineKey = this.dataset.lineKey;
      if (lineKey) {
        this._removeLineItem(container, lineKey);
      }
      return;
    }
    
    const input = container.querySelector('input[is="quantity-input"]');
    if (!input) return;
    
    if (action === "increment") {
      input.stepUp();
    } else if (action === "decrement") {
      input.stepDown();
    }
    
    // Dispatch change event to trigger cart update
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  async _removeLineItem(container, lineKey) {
    // If on cart page, redirect to cart change URL
    if (window.themeVariables?.settings?.pageType === "cart") {
      window.location.href = `${Shopify.routes.root}cart/change?id=${lineKey}&quantity=0`;
      return;
    }

    // Otherwise, do AJAX removal (e.g., in cart drawer)
    const lineItem = container.closest("line-item");
    lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity: 0 } }));

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

    const cartContent = await (await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sectionsToBundle
      })
    })).json();

    lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
      bubbles: true,
      detail: {
        quantity: 0,
        cart: cartContent
      }
    }));

    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: "line-item:change",
        cart: cartContent
      }
    }));
  }
}

if (!window.customElements.get("quantity-button")) {
  window.customElements.define("quantity-button", QuantityButton, { extends: "button" });
}

/**
 * Loading animation for quick-buy variant selector buttons.
 * Shows a 1-second loading spinner when clicking to open the variant drawer.
 */
document.addEventListener("DOMContentLoaded", function() {
  document.addEventListener("click", function(event) {
    // Check if clicked element is inside a custom-atc with aria-controls (variant selector)
    const customAtc = event.target.closest(".custom-atc[aria-controls]");
    if (!customAtc) return;
    
    const button = customAtc.querySelector("button");
    if (!button || button.getAttribute("aria-busy") === "true") return;
    
    // Set loading state
    button.setAttribute("aria-busy", "true");
    
    // Remove loading state after 1 second
    setTimeout(() => {
      button.setAttribute("aria-busy", "false");
    }, 1000);
  });
});

/**
 * Success state feedback for cart product slider ATC buttons.
 * Shows "Added ✓" for 2 seconds after successfully adding to cart.
 * This provides immediate visual feedback in the cart drawer where
 * the user might not see the cart update at the top.
 * 
 * Note: The cart drawer refreshes its content after adding items,
 * so we need to track the product handle and slide index, then
 * restore the state after the DOM is updated.
 */
document.addEventListener("DOMContentLoaded", function() {
  // Track product info for restoring state after cart refresh
  let pendingSuccessState = null;

  // Track clicks on custom-atc buttons within cart-product-slider
  document.addEventListener("click", function(event) {
    const cartProductSlider = event.target.closest("cart-product-slider");
    if (!cartProductSlider) return;

    const customAtc = event.target.closest(".custom-atc");
    if (!customAtc) return;

    const productCard = event.target.closest("product-card");
    if (!productCard) return;

    const productHandle = productCard.getAttribute("handle");
    const swiperSlide = productCard.closest(".swiper-slide");
    const slideIndex = swiperSlide ? 
      Array.from(swiperSlide.parentElement.children).indexOf(swiperSlide) : 0;

    // Store info for after cart refresh
    pendingSuccessState = {
      productHandle: productHandle,
      slideIndex: slideIndex
    };
  });

  // Listen for successful cart additions
  document.addEventListener("variant:add", function(event) {
    if (!pendingSuccessState) return;

    const { productHandle, slideIndex } = pendingSuccessState;
    pendingSuccessState = null;

    // Use requestAnimationFrame to wait for the DOM to be updated
    // The cart drawer updates with setTimeout 0, so we need to wait
    // a bit longer for the DOM to fully refresh
    setTimeout(() => {
      requestAnimationFrame(() => {
        const cartProductSlider = document.querySelector("cart-product-slider");
        if (!cartProductSlider) return;

        // Find the product card with matching handle
        const productCard = cartProductSlider.querySelector(`product-card[handle="${productHandle}"]`);
        if (!productCard) return;

        // Find the button in this product card
        const customAtc = productCard.querySelector(".custom-atc");
        if (!customAtc) return;

        const button = customAtc.querySelector("button");
        if (!button) return;

        // Show success state
        button.removeAttribute("aria-busy");
        button.setAttribute("data-success", "true");

        // Restore slider position
        if (cartProductSlider.swiper && slideIndex > 0) {
          cartProductSlider.swiper.slideTo(slideIndex, 0);
        }

        // Reset after 2 seconds
        setTimeout(() => {
          button.removeAttribute("data-success");
        }, 2000);
      });
    }, 50); // Small delay to ensure cart drawer has finished updating
  });
});

/**
 * Article Product Card Embed
 * 
 * Allows merchants to embed product cards within article body content
 * by using the syntax: [[product-handle]]
 * 
 * Example: [[camping-tent-pro]] will embed a product card for that product.
 * 
 * The script scans .prose content (article body) for these patterns,
 * creates placeholder elements, fetches the product card via Section Rendering API,
 * and replaces the placeholders with actual product cards.
 */
class ArticleProductEmbed {
  constructor() {
    this.PATTERN = /\[\[([a-zA-Z0-9\-_]+)\]\]/g;
    this.SECTION_ID = 'product-card-embed';
    this.processedElements = new WeakSet();
  }

  init() {
    // Process on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.processArticleContent());
    } else {
      this.processArticleContent();
    }
  }

  async processArticleContent() {
    // Find all .prose elements (article body content)
    const proseElements = document.querySelectorAll('.article .prose');
    
    for (const prose of proseElements) {
      if (this.processedElements.has(prose)) continue;
      this.processedElements.add(prose);
      
      await this.processElement(prose);
    }
  }

  async processElement(element) {
    // Get all text nodes that might contain our pattern
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      if (this.PATTERN.test(node.textContent)) {
        textNodes.push(node);
      }
      // Reset regex lastIndex for next test
      this.PATTERN.lastIndex = 0;
    }

    // Process each text node
    for (const textNode of textNodes) {
      await this.processTextNode(textNode);
    }
  }

  async processTextNode(textNode) {
    const text = textNode.textContent;
    const matches = [...text.matchAll(this.PATTERN)];
    
    if (matches.length === 0) return;

    // Create a document fragment to hold the new content
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    for (const match of matches) {
      const [fullMatch, productHandle] = match;
      const matchIndex = match.index;

      // Add text before the match
      if (matchIndex > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.substring(lastIndex, matchIndex))
        );
      }

      // Create placeholder for the product card
      const placeholder = document.createElement('div');
      placeholder.className = 'product-card-embed-placeholder';
      placeholder.setAttribute('data-product-handle', productHandle);
      placeholder.innerHTML = `
        <div class="product-card-embed-loading">
          <span class="loading-spinner"></span>
          <span>Loading product...</span>
        </div>
      `;
      fragment.appendChild(placeholder);

      // Fetch and replace (async, doesn't block)
      this.fetchAndReplaceProductCard(placeholder, productHandle);

      lastIndex = matchIndex + fullMatch.length;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
      fragment.appendChild(
        document.createTextNode(text.substring(lastIndex))
      );
    }

    // Replace the text node with our fragment
    textNode.parentNode.replaceChild(fragment, textNode);
  }

  async fetchAndReplaceProductCard(placeholder, productHandle) {
    try {
      // Fetch the product card section using Section Rendering API
      const url = `${window.Shopify.routes.root}products/${productHandle}?section_id=${this.SECTION_ID}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const html = await response.text();
      
      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productCardEmbed = doc.querySelector('.product-card-embed');

      if (productCardEmbed) {
        // Create wrapper with proper styling class
        const wrapper = document.createElement('div');
        wrapper.className = 'article-product-card-wrapper';
        wrapper.innerHTML = productCardEmbed.outerHTML;
        
        // Replace placeholder with actual product card
        placeholder.replaceWith(wrapper);
        
        // Trigger any necessary initialization (e.g., for quick-buy functionality)
        if (window.Shopify?.PaymentButton) {
          window.Shopify.PaymentButton.init();
        }
      } else {
        // Product not found or error in rendering
        placeholder.innerHTML = `
          <div class="product-card-embed product-card-embed--error">
            <p>Unable to load product: ${productHandle}</p>
          </div>
        `;
      }
    } catch (error) {
      console.error(`Error loading product card for "${productHandle}":`, error);
      placeholder.innerHTML = `
        <div class="product-card-embed product-card-embed--error">
          <p>Unable to load product: ${productHandle}</p>
        </div>
      `;
    }
  }
}

// Initialize the article product embed
const articleProductEmbed = new ArticleProductEmbed();
articleProductEmbed.init();
